import axios from 'axios';
import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import type { AuthState } from '@/types/auth';

// ---------------------------------------------------------------------------
// Normalised API error shape (matches backend global exception filter)
// ---------------------------------------------------------------------------

export interface ApiError {
  statusCode: number;
  message: string | string[];
  error: string;
  timestamp: string;
  path: string;
}

// ---------------------------------------------------------------------------
// Auth store accessor
//
// The concrete Zustand store is created in Module 7.4. We hold a lazy
// reference here so the API client can read the token and call logout()
// without a circular import at module-load time.
// ---------------------------------------------------------------------------

type AuthStoreAccessor = () => AuthState;

let getAuthState: AuthStoreAccessor | null = null;

/**
 * Called once by the auth store module (7.4) to register itself.
 * This avoids a circular dependency between 7.3 and 7.4.
 */
export function registerAuthStore(accessor: AuthStoreAccessor): void {
  getAuthState = accessor;
}

// ---------------------------------------------------------------------------
// Router navigate accessor
//
// React Router's navigate() is component-scoped. The auth flow (7.4/7.5)
// registers a navigate function so the interceptor can redirect on 401.
// ---------------------------------------------------------------------------

type NavigateFn = (path: string) => void;

let navigateFn: NavigateFn | null = null;

/**
 * Called once from the app shell to register React Router's navigate function.
 */
export function registerNavigate(fn: NavigateFn): void {
  navigateFn = fn;
}

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------

const baseURL: string = import.meta.env.VITE_BACKEND_URL;
const timeout = 30_000;

export const apiClient: AxiosInstance = axios.create({
  baseURL,
  timeout,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // TODO: Phase 8 — enable refresh token flow with HTTP-only cookies
});

// ---------------------------------------------------------------------------
// Request interceptor — inject Bearer token
// ---------------------------------------------------------------------------

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const token = getAuthState?.()?.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: unknown) => Promise.reject(error),
);

// ---------------------------------------------------------------------------
// Response interceptor — handle 401, normalise errors
//
// The interceptor returns the full AxiosResponse (preserving the AxiosInstance
// contract). Envelope unwrapping (response.data) happens in the typed api
// wrapper methods below.
//
// 401 reason mapping — keep in sync with the backend auth module. The behaviour
// columns below are enforced by the response interceptor's 401 branch.
//
// | Source                                  | Message string                       | Frontend behaviour                                    |
// |-----------------------------------------|--------------------------------------|-------------------------------------------------------|
// | jwt-auth.guard.ts:11-12 (jwt expired)   | `Token expired`                      | Default branch — attempt silent refresh, replay req.  |
// | jwt-auth.guard.ts:14 (other jwt fail)   | `Unauthorized`                       | Default branch — attempt refresh; if structurally     |
// |                                         |                                      | broken, refresh fails and hard-logout fires.          |
// | jwt.strategy.ts:47 (Redis down)         | `Authentication service unavailable` | Surface to caller, no logout, no refresh attempt.     |
// |                                         |                                      | On the refresh call itself: same (§3 D5).             |
// | jwt.strategy.ts:51 (JTI blacklisted)    | `Token revoked`                      | Hard logout — refresh would not help.                 |
// | auth.service.ts:63,73,110,115,274,281   | `Invalid credentials`                | N/A — login-time, handled by login form.              |
// | auth.service.ts:164,169,178 (refresh)   | `Token revoked`                      | Hard logout (refresh call's `isRefreshCall` branch).  |
// | auth.service.ts:68,73 (Keycloak login)  | `User not authorized for CBS`        | N/A — login-time, Keycloak path.                      |
// | keycloak.service.ts                     | `Invalid Keycloak token`             | N/A — login-time, Keycloak path.                      |
// | keycloak.service.ts                     | `JWKS error`                         | N/A — login-time, Keycloak path.                      |
// | keycloak.service.ts                     | `Missing kid in token header`        | N/A — login-time, Keycloak path.                      |
// | Tenant guard, role guard                | (403 not 401)                        | n/a                                                   |
//
// Fragile by message-string per §3 D6 — revisit when the backend exposes an
// error-code enum.
// ---------------------------------------------------------------------------

let isRedirectingToLogin = false;

/**
 * Outcome of a silent-refresh attempt — a discriminated union so callers can
 * distinguish "Redis blip, transient" from "token genuinely dead, hard logout"
 * (per builder-prompt §3 D5). The original prompt template returned
 * `string | null`, but since performRefresh uses raw axios.post (bypassing the
 * apiClient interceptor), the interceptor's `isRefreshCall` branch can't
 * differentiate the transient case for the silent-refresh path — the
 * distinction has to live in performRefresh's return value.
 */
type RefreshOutcome =
  | { kind: 'success'; accessToken: string }
  | { kind: 'stale' }
  | { kind: 'transient' }
  | { kind: 'fatal' };

let refreshPromise: Promise<RefreshOutcome> | null = null;

/**
 * Performs the silent refresh. Single-flight: concurrent 401s during the same
 * refresh window all await the same promise.
 *
 * HTTP-only cookies are a follow-up; sessionStorage is a deliberate interim
 * per builder-prompt §3 D2. XSS now leaks a 7d refresh chain, not a 120s
 * window — accept this only until D7 + cookies ship.
 *
 * The refresh call presents the refresh token in the Authorization header
 * (the backend's JwtAuthGuard accepts either token type — see backend
 * auth.service.ts:134-188).
 */
async function performRefresh(): Promise<RefreshOutcome> {
  if (refreshPromise) {
    return refreshPromise;
  }
  refreshPromise = (async (): Promise<RefreshOutcome> => {
    try {
      const state = getAuthState?.();
      const refreshToken = state?.refreshToken;
      if (!refreshToken) return { kind: 'fatal' };

      // Use a raw axios call to avoid recursing through this interceptor.
      const response = await axios.post<{
        accessToken: string;
        refreshToken: string;
      }>(
        `${baseURL}/api/auth/refresh`,
        {},
        {
          headers: { Authorization: `Bearer ${refreshToken}` },
          timeout,
        },
      );
      const latestState = getAuthState?.();
      if (latestState?.refreshToken !== refreshToken) {
        return { kind: 'stale' };
      }
      latestState.setTokens?.(response.data.accessToken, response.data.refreshToken);
      return { kind: 'success', accessToken: response.data.accessToken };
    } catch (err) {
      // Transient = anything we'd rather the user retry on than be logged
      // out for. Covers three cases:
      //   1. §3 D5: 401 'Authentication service unavailable' (Redis blip)
      //   2. Network failure (no err.response — backend unreachable,
      //      mid-deploy, transient connectivity)
      //   3. 5xx from the backend (the auth service is up but unhappy)
      // Hard-logging-out the org on any of these would replay the bug we
      // built this feature to fix.
      if (axios.isAxiosError(err)) {
        if (!err.response) {
          return { kind: 'transient' };
        }
        if (err.response.status >= 500) {
          return { kind: 'transient' };
        }
        if (err.response.status === 401) {
          const data = err.response.data as { message?: unknown } | undefined;
          if (data?.message === 'Authentication service unavailable') {
            return { kind: 'transient' };
          }
        }
      }
      return { kind: 'fatal' };
    } finally {
      // Reset gate after either outcome so the next genuine expiry can refresh.
      // Tiny tail delay so any queued waiters resolve against the same outcome.
      setTimeout(() => {
        refreshPromise = null;
      }, 0);
    }
  })();
  return refreshPromise;
}

function hardLogoutAndReject(apiError: ApiError): Promise<never> {
  const state = getAuthState?.();
  const hadToken = !!state?.token;
  if (hadToken) {
    state!.logout();
  }
  if (hadToken && !isRedirectingToLogin) {
    isRedirectingToLogin = true;
    navigateFn?.('/login');
    setTimeout(() => {
      isRedirectingToLogin = false;
    }, 100);
  }
  return Promise.reject(apiError);
}

function asTransientAuthUnavailable(apiError: ApiError): ApiError {
  return {
    ...apiError,
    statusCode: 401,
    message: 'Authentication service unavailable',
    error: 'Unauthorized',
  };
}

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,

  // Error: normalise then route by status. The handler is async so it can
  // await the silent refresh on 401.
  async (error: unknown) => {
    if (!axios.isAxiosError(error)) {
      return Promise.reject(error);
    }

    const status = error.response?.status;
    const responseData = error.response?.data as Record<string, unknown> | undefined;
    const requestUrl = error.config?.url ?? 'unknown';

    // Resolve message — preserve string[] from backend validation errors
    let message: string | string[];
    if (Array.isArray(responseData?.message)) {
      message = responseData.message as string[];
    } else if (typeof responseData?.message === 'string') {
      message = responseData.message;
    } else {
      message = error.message || 'Request failed';
    }

    // Build normalised error — prefer server-supplied timestamp/path for correlation
    const apiError: ApiError = {
      statusCode: status ?? 0,
      message,
      error: typeof responseData?.error === 'string' ? responseData.error : 'Unknown error',
      timestamp:
        typeof responseData?.timestamp === 'string'
          ? responseData.timestamp
          : new Date().toISOString(),
      path: typeof responseData?.path === 'string' ? responseData.path : requestUrl,
    };

    // 401 — try silent refresh once, then replay; hard logout on failure
    if (status === 401) {
      const requestConfig = error.config as
        | (InternalAxiosRequestConfig & { _retried?: boolean })
        | undefined;
      const isRefreshCall = requestConfig?.url?.endsWith('/api/auth/refresh') === true;
      const alreadyRetried = requestConfig?._retried === true;
      const responseMessage = typeof responseData?.message === 'string' ? responseData.message : '';

      // 401 on the refresh call itself → usually hard logout, BUT if the
      // backend is signalling a transient Redis outage we must NOT wipe auth
      // state. Otherwise a Redis blip at the refresh boundary mass-logs-out
      // every active operator. See §3 D5.
      if (isRefreshCall) {
        if (responseMessage === 'Authentication service unavailable') {
          return Promise.reject(apiError);
        }
        return hardLogoutAndReject(apiError);
      }

      // Hard-logout messages from the backend — do not attempt refresh
      // (the refresh would just re-fail for the same reason).
      // Mapping per builder-prompt §3 D6 — fragile by message string; revisit
      // when the backend exposes an error-code enum.
      if (responseMessage === 'Token revoked') {
        return hardLogoutAndReject(apiError);
      }

      // Auth service unavailable (Redis blip) — surface to caller without
      // logging out. The caller (React Query) can retry on the user's next
      // action. Auth state stays intact.
      if (responseMessage === 'Authentication service unavailable') {
        return Promise.reject(apiError);
      }

      // Default path: attempt silent refresh once, replay the original request.
      if (!alreadyRetried && requestConfig) {
        const refreshOutcome = await performRefresh();
        if (refreshOutcome.kind === 'success') {
          requestConfig._retried = true;
          requestConfig.headers.Authorization = `Bearer ${refreshOutcome.accessToken}`;
          return apiClient.request(requestConfig);
        }
        if (refreshOutcome.kind === 'transient') {
          // §3 D5: Redis blip during refresh — surface to caller, keep
          // auth state intact so the next user action can re-attempt.
          return Promise.reject(asTransientAuthUnavailable(apiError));
        }
        if (refreshOutcome.kind === 'stale') {
          // User logged out, timed out, switched tenant, or logged in again
          // while refresh was in flight. Do not replay the old request and do
          // not hard-logout the current store state.
          return Promise.reject(apiError);
        }
      }

      // Refresh failed (fatal) or already retried — hard logout.
      return hardLogoutAndReject(apiError);
    }

    // Log 5xx errors for debugging (no secrets — only status and path)
    if (status !== undefined && status >= 500) {
      console.error(`[api-client] Server error ${String(status)} on ${requestUrl}`);
    }

    return Promise.reject(apiError);
  },
);

// ---------------------------------------------------------------------------
// Typed convenience wrappers
//
// These unwrap the Axios response envelope (response.data) and provide
// generic typing for callers. The raw apiClient preserves the standard
// AxiosInstance contract (returns AxiosResponse).
// ---------------------------------------------------------------------------

export const api = {
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await apiClient.get<T>(url, config);
    return response.data;
  },

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await apiClient.post<T>(url, data, config);
    return response.data;
  },

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await apiClient.put<T>(url, data, config);
    return response.data;
  },

  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await apiClient.patch<T>(url, data, config);
    return response.data;
  },

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await apiClient.delete<T>(url, config);
    return response.data;
  },
};
